import React, { useRef, useState, useCallback, useEffect } from "react";
import { Upload, Image as ImageIcon, FolderOpen, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import MediaPicker from "./MediaPicker";
import { uploadFilesInBatches } from "@/lib/uploadQueue";

/**
 * Reusable image-input field. Drop-in replacement for "paste a URL" text inputs.
 * Shows a thumbnail (if value) + drag-drop zone + Upload button + Pick from Library button.
 *
 * Props:
 *   label?:   string            — small label above
 *   value:    string            — current URL (e.g. /api/files/.../medium.webp OR external)
 *   onChange: (url) => void
 *   aspect?:  string            — tailwind aspect class, default "aspect-[4/3]"
 *   variant?: "medium"|"large"  — which size to use for newly uploaded images (default "large" for covers)
 *   testId?:  string
 */
export default function ImageField({ label, value, onChange, aspect = "aspect-[4/3]", variant = "large", testId }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // 0..100
  const [picking, setPicking] = useState(false);
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    setUploading(true); setProgress(0);
    const { ok, results } = await uploadFilesInBatches({
      files: [file],
      concurrency: 1,
      maxRetries: 2,
      onProgress: (_id, pct) => setProgress(pct),
    });
    setUploading(false);
    if (ok && results[0]?.data) {
      const r = results[0].data;
      const url = r.urls?.[variant] || r.url;
      onChange(url);
      toast.success("Image uploaded");
    } else {
      const err = results[0]?.error;
      toast.error(err?.userMessage || err?.response?.data?.detail || err?.message || "Upload failed");
    }
    setProgress(0);
  };

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      prevent(e);
      el.classList.remove("border-[#D4AF37]", "bg-[#D4AF37]/5");
      const file = Array.from(e.dataTransfer?.files || []).find(f => f.type.startsWith("image/"));
      handleUpload(file);
    };
    const onOver = (e) => { prevent(e); el.classList.add("border-[#D4AF37]", "bg-[#D4AF37]/5"); };
    const onLeave = (e) => { prevent(e); el.classList.remove("border-[#D4AF37]", "bg-[#D4AF37]/5"); };
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [variant]); // eslint-disable-line

  const pickFromLibrary = useCallback((m) => {
    const url = m.urls?.[variant] || m.url;
    onChange(url);
  }, [onChange, variant]);

  return (
    <div data-testid={testId}>
      {label && <div className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60 mb-2">{label}</div>}
      <div ref={dropRef}
        className={`relative ${aspect} bg-black border-2 border-dashed border-white/15 transition-colors overflow-hidden`}>
        {value ? (
          <>
            <img src={value} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange("")} title="Remove"
              className="absolute top-2 right-2 bg-black/70 hover:bg-red-500 text-white p-2"
              data-testid={`${testId || "image"}-clear`}>
              <XIcon size={14}/>
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50 p-4 text-center">
            <ImageIcon size={32} className="opacity-50 mb-2" />
            <div className="text-sm">{uploading ? `Uploading… ${progress}%` : "Drop an image here, or use the buttons below"}</div>
            <div className="text-xs text-white/30 mt-1">JPG, PNG, WebP — auto-optimized to WebP</div>
            {uploading && (
              <div className="absolute bottom-0 inset-x-0 h-1 bg-white/10">
                <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        )}
      </div>
      <input type="file" accept="image/*" ref={fileRef} className="hidden"
        onChange={e => handleUpload(e.target.files?.[0])} data-testid={`${testId || "image"}-input`} />
      <div className="flex gap-2 mt-2">
        <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="btn-ghost flex-1 justify-center" data-testid={`${testId || "image"}-upload-btn`}>
          <Upload size={12}/> {uploading ? "Uploading..." : "Upload from Computer"}
        </button>
        <button type="button" onClick={() => setPicking(true)} className="btn-ghost flex-1 justify-center"
          data-testid={`${testId || "image"}-library-btn`}>
          <FolderOpen size={12}/> Media Library
        </button>
      </div>

      {picking && <MediaPicker onClose={() => setPicking(false)} onSelect={pickFromLibrary} />}
    </div>
  );
}
